import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  ListOl,
  ListUl,
} from "@gravity-ui/icons";
import { Icon, type IconData } from "@gravity-ui/uikit";
import { useEditorState, type Editor } from "@tiptap/react";
import { type MouseEvent } from "react";

import css from "./RichTemplateEditor.module.scss";

interface ToolbarState {
  bold: boolean;
  italic: boolean;
  bulletList: boolean;
  orderedList: boolean;
}

const DEFAULT_TOOLBAR_STATE: ToolbarState = {
  bold: false,
  italic: false,
  bulletList: false,
  orderedList: false,
};

interface ToolbarProps {
  editor: Editor | null;
}

/**
 * Тулбар форматирования. Подписан на `isActive`-флаги через `useEditorState`
 * с selector'ом — это единственная точка, которой нужны транзакционные
 * перерисовки. Сам редактор `shouldRerenderOnTransaction=false` (default),
 * поэтому остальное дерево лишних рендеров не получает.
 */
export const Toolbar = ({ editor }: ToolbarProps) => {
  const state =
    useEditorState<ToolbarState | null>({
      editor,
      selector: ({ editor: e }) =>
        e
          ? {
              bold: e.isActive("bold"),
              italic: e.isActive("italic"),
              bulletList: e.isActive("bulletList"),
              orderedList: e.isActive("orderedList"),
            }
          : null,
    }) ?? DEFAULT_TOOLBAR_STATE;

  return (
    <div className={css.toolbar} role="toolbar" aria-label="Форматирование">
      <ToolbarButton
        editor={editor}
        icon={BoldIcon}
        active={state.bold}
        title="Жирный (Ctrl+B)"
        run={(e) => e.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        editor={editor}
        icon={ItalicIcon}
        active={state.italic}
        title="Курсив (Ctrl+I)"
        run={(e) => e.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        editor={editor}
        icon={ListUl}
        active={state.bulletList}
        title="Маркированный список"
        run={(e) => e.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        editor={editor}
        icon={ListOl}
        active={state.orderedList}
        title="Нумерованный список"
        run={(e) => e.chain().focus().toggleOrderedList().run()}
      />
    </div>
  );
};

interface ToolbarButtonProps {
  editor: Editor | null;
  icon: IconData;
  active: boolean;
  title: string;
  run: (editor: Editor) => boolean;
}

// onMouseDown + preventDefault обязателен: иначе фокус-blur на contenteditable
// случится ДО запуска команды, selection потеряется, ничего не вставится.
const ToolbarButton = ({
  editor,
  icon,
  active,
  title,
  run,
}: ToolbarButtonProps) => {
  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    if (editor) run(editor);
  };
  return (
    <button
      type="button"
      className={active ? css.btnActive : css.btn}
      onMouseDown={onMouseDown}
      title={title}
      aria-pressed={active}
      aria-label={title}
      disabled={!editor}
    >
      <Icon data={icon} size={14} />
    </button>
  );
};
